import i18next, { type InitOptions } from 'i18next';
import en from '../locales/en.json';
import fr from '../locales/fr.json';
import { isLanguageCode, readLanguageCookie, writeLanguageCookie } from './language-cookie';

export interface InitI18nOptions {
  lng?: 'en' | 'fr';
  fallbackLng?: 'en' | 'fr';
  /**
   * When set, the active language is read from (and persisted to) a cookie of
   * this name. The cookie wins over `lng` on load, and every subsequent
   * `changeLanguage` rewrites it — so the language switcher just needs to call
   * `i18n.changeLanguage(...)`; persistence happens here.
   */
  languageCookie?: string;
  extra?: InitOptions;
}

/**
 * Best-effort language from the browser's UI settings, mapped to a supported
 * code. Checks `navigator.languages` (ordered by preference) then
 * `navigator.language`. Returns `null` outside a browser (SSR/native) or when no
 * entry maps to 'en'/'fr', so the caller can fall back to its own default.
 */
export function detectBrowserLanguage(): 'en' | 'fr' | null {
  if (typeof navigator === 'undefined') return null;
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const base = tag?.toLowerCase().split('-')[0];
    if (base === 'en' || base === 'fr') return base;
  }
  return null;
}

export async function initI18n(opts: InitI18nOptions = {}): Promise<typeof i18next> {
  // Precedence: saved cookie › explicit `lng` › browser preference › 'en'. The
  // browser check means a first-time visitor with a French browser lands in
  // French (e.g. the registration form) instead of always defaulting to English.
  const cookieLng = opts.languageCookie ? readLanguageCookie(opts.languageCookie) : null;
  const initialLng = cookieLng ?? opts.lng ?? detectBrowserLanguage() ?? 'en';

  await i18next.init({
    lng: initialLng,
    fallbackLng: opts.fallbackLng ?? 'en',
    resources: {
      en: { translation: en },
      fr: { translation: fr },
    },
    // i18next reads `ns:key` by default, which collides with every permission
    // name in the access model — they are `resource:action` strings, and
    // `t('gov.admin.permissions.officer:appoint')` was being parsed as namespace
    // `gov.admin.permissions.officer` plus key `appoint`. The lookup then missed
    // and fell back to the fragment after the colon, so the gov "My access"
    // panel rendered two adjacent chips both reading "appoint" (for
    // `officer:appoint` and `member:appoint`), and the admin access-control
    // matrix — which asks for these with `defaultValue: ''` — silently showed no
    // descriptions at all. 42 keys across both languages, none of them reachable.
    //
    // Everything here lives in the single default `translation` namespace and no
    // call site uses `ns:key` syntax, so the separator buys nothing and costs a
    // whole category of keys. `keySeparator` stays `.` — the dotted paths need it.
    nsSeparator: false,
    interpolation: { escapeValue: false },
    ...opts.extra,
  });

  // `<html lang>` must always match the language actually on screen (WCAG 3.1.1
  // / 3.1.2) — a French page announced with an English voice is a direct AA
  // failure, and it is the single most common bilingual-app defect. This used
  // to be wired up only for apps that passed `languageCookie`, which left the
  // gov and admin portals permanently reporting `lang="en"` from their static
  // `index.html`. It is unconditional now; cookie persistence stays opt-in.
  const syncDocumentLang = (lng: string) => {
    if (typeof document !== 'undefined') document.documentElement.lang = lng;
  };
  syncDocumentLang(initialLng);
  i18next.on('languageChanged', syncDocumentLang);

  if (opts.languageCookie) {
    const cookieName = opts.languageCookie;
    const persist = (lng: string) => {
      if (isLanguageCode(lng)) writeLanguageCookie(cookieName, lng);
    };
    persist(initialLng);
    i18next.on('languageChanged', persist);
  }

  return i18next;
}
